// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { avoidBreakInsidePrintElement, getSystemColor, xScreen } from "@guestsupply/XDL/Utilities";
import { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

interface OrderInformationStyles {
    label?: TypographyProps;
    value?: TypographyProps;
    labelWithDots?: TypographyProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    address?: {
        heading?: TypographyProps;
        informationTitle?: TypographyProps;
        divider?: GridItemProps;
    } & AddressInfoDisplayStyles;
    note?: { wrapper?: InjectableCss; title?: TypographyProps; text?: TypographyProps };
    orderSummary?: any;
    groupLines?: {
        groupTitle?: InjectableCss;
        groupWrapper?: InjectableCss;
    };
}

const xOrderInformationStyles: OrderInformationStyles = {
    label: {
        size: 14,
        lineHeight: "24px",
        weight: "bold",
        css: css`
            margin-right: 4px;
            @media print {
                display: block;
            }
        `,
    },
    value: {
        size: 14,
        lineHeight: "24px",
        css: css`
            &:empty:after {
                content: "- -";
            }
            @media print {
                display: inline;
                width: auto;
            }
        `,
    },
    address: {
        informationTitle: {
            size: 16,
            weight: "bold",
            as: "h2",
        },
        heading: {
            size: 16,
            lineHeight: "17px",
            weight: "bold",
        },
        divider: {
            className: "divider-item",
            width: 12,
            css: css`
                &&& {
                    border-top: 1px solid ${getColor("common.border")};
                    margin: 8px 0 12px 0;
                    padding: 0;
                }
            `,
        },
    },
    note: {
        text: {
            size: 14,
            lineHeight: "22px",
        },
    },
    orderSummary: {
        css: css`
            ${avoidBreakInsidePrintElement}
            ${xScreen("lte", "md")} {
                margin-bottom: 10px;
            }
        `,
    },
    groupLines: {
        groupTitle: {
            css: css`
                color: ${getColor("text.main")};
                background: ${getColor("common.ui2")};
                font-size: 12px;
                letter-spacing: 0;
                line-height: 32px;
                padding-left: 15px;
                font-weight: 500;
                margin-top: 10px;
                border-radius: 4px;
                @media print {
                    color: ${getSystemColor("text.main")};
                    background-color: ${getSystemColor("common.ui2")};
                }
            `,
        },
        groupWrapper: {
            css: css`
                div:last-child {
                    border-bottom: none;
                }
            `,
        },
    },
};

xOrderInformationStyles.labelWithDots = {
    size: 16,
    lineHeight: "22px",
    weight: "bold",
    transform: "none",
    color: "text",
    css: css`
        margin-right: 4px;
        white-space: nowrap;
        &:after {
            content: ":";
        }
        @media print {
            display: inline;
            width: auto;
        }
    `,
};

xOrderInformationStyles.headingAndTextStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            margin-bottom: 4px;
            @media print {
                display: block;
            }
        `,
    },
    heading: {
        ...xOrderInformationStyles.labelWithDots,
    },
    text: {
        ...xOrderInformationStyles.value,
    },
};

export default xOrderInformationStyles;
